import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  fetchOrganizationSessionsReport,
  type OrgSessionReportRow,
  type OrgSessionReportTotals,
  type OrgSessionsReportDriver,
  type OrganizationUnitSummary,
} from "@/lib/api";
import { HttpError } from "@/lib/http";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Download, AlertTriangle } from "@/components/icons";
import { ColumnChooser } from "@/components/ColumnChooser";
import { useColumnConfig } from "@/hooks/useColumnConfig";
import {
  SESSION_REPORT_COLUMNS,
  SESSION_REPORT_COLUMNS_LS_KEY,
  SESSION_REPORT_DEFAULT_VISIBILITY,
  buildReportCsv,
  money,
  visibleReportColumns,
  type ReportCellCtx,
  type ReportColumnCtx,
} from "@/views/orgSessionsReportColumns";
import { useLocalization } from "@voltie/localization-web";
import { localization, type Translate } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";

// Same "has a real custom name" rule as the dashboard unit table: a name that still
// equals the auto-generated "Unit <id>" placeholder doesn't count.
function customUnitName(u: OrganizationUnitSummary): string | null {
  return u.name && u.name !== `Unit ${u.unit_id}` ? u.name : null;
}

// A 4xx is the server rejecting the request (bad range, unit not in the org): the same
// request will fail again, so it is shown at once instead of after silent retries.
function retryReport(failureCount: number, error: unknown): boolean {
  if (error instanceof HttpError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 2;
}

/**
 * In-UI charging-sessions report for an organization.
 *
 * Every number comes from the server: the attention filter is a server-side query
 * parameter, so the rows, the totals row and the export are ONE selection, and the
 * totals of the whole period are a separate, labelled line. A ColumnChooser (like the
 * Devices page) picks which columns to show; the choice persists per browser. "Export
 * CSV" writes the report currently shown — its applied range and filters — with
 * exactly the visible columns, in the shown order; it never re-reads the date fields.
 * A Payment column surfaces each session's payment status and, for money that never
 * captured, the diagnostics to chase it.
 *
 * `canSeeMoney` is the view scope. A system admin (true) sees the full report. An
 * organization manager (false) sees a SESSION-ONLY view: the money/fee/payout/payment
 * columns are removed from the table, the column chooser and the CSV. This is cosmetic
 * scoping only — the server also withholds the money DATA for a manager-scope request,
 * so the gate does not depend on the client.
 */
export function OrgSessionsReport({
  orgId,
  units,
  canSeeMoney = true,
}: {
  orgId: number;
  /** Org units (with custom names) so the table can label a row by name and populate
   *  the "by unit" filter. */
  units?: OrganizationUnitSummary[];
  /** Whether this viewer may see money (admin). Default true keeps the admin call
   *  sites unchanged; the org page passes canSeeMoney={isAdmin}. */
  canSeeMoney?: boolean;
}) {
  // language/revision rebuild the memoized filter options after a catalog switch.
  const { t, language, revision } = useLocalization(localization);
  // The columns this viewer may see at all. A manager gets the money-free subset; the
  // table, chooser and CSV all derive from this, so a persisted visibility choice can
  // never surface a money column for a manager.
  const availableColumns = useMemo(
    () => (canSeeMoney ? SESSION_REPORT_COLUMNS : SESSION_REPORT_COLUMNS.filter((c) => !c.money)),
    [canSeeMoney],
  );
  // ColumnChooser only needs each column's id + label (rebuilt after a catalog switch).
  const chooserColumns = useMemo(
    () => availableColumns.map((c) => ({ id: c.id, label: t(c.labelKey) })),
    [availableColumns, t, language, revision],
  );
  // unit_id -> custom name, for labelling report rows.
  const unitNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of units ?? []) {
      const name = customUnitName(u);
      if (name) m.set(u.unit_id, name);
    }
    return m;
  }, [units]);

  // Lookup context the column CSV renderers need; the cells also get the active `t`.
  const columnCtx: ReportColumnCtx = useMemo(
    () => ({ unitName: (unitId: string) => unitNames.get(unitId) ?? null }),
    [unitNames],
  );
  const cellCtx: ReportCellCtx = { ...columnCtx, t };

  // Column visibility (persisted per browser, keyed separately from the Devices table).
  const { columnVisibility, setColumnVisibility, resetToDefaults } = useColumnConfig(
    SESSION_REPORT_COLUMNS_LS_KEY,
    SESSION_REPORT_DEFAULT_VISIBILITY,
  );
  const handleColumnToggle = (colId: string, visible: boolean) =>
    setColumnVisibility((prev) => ({ ...prev, [colId]: visible }));
  const visibleCols = useMemo(
    () => visibleReportColumns(columnVisibility, availableColumns),
    [columnVisibility, availableColumns],
  );
  const colCount = Math.max(visibleCols.length, 1);

  // Filter dropdown options. "" = All (keeps the report org-wide, as before).
  const unitOptions = useMemo(
    () => [
      { value: "", label: t("operator.orgs.report.all_units") },
      ...(units ?? []).map((u) => ({
        value: u.unit_id,
        label: customUnitName(u) ?? u.unit_id,
      })),
    ],
    [units, t, language, revision],
  );
  // Selected filters ("" = all). Server-side: a change re-runs the query.
  const [unitFilter, setUnitFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  // Driver options come from the report itself: everyone who charged in the shown range
  // (and unit filter) on this org's units, members or not, under the name the rows
  // show. They are kept between responses (a refetch for another filter has no data
  // yet), and the names seen are remembered so a selected driver with no sessions in a
  // new range stays selectable instead of silently vanishing. Both belong to one org.
  // `names` holds the server's names (null = unnamed); labels are built at render.
  const [driverMemo, setDriverMemo] = useState<{
    orgId: number;
    from?: OrgSessionsReportDriver[];
    names: Record<string, string | null>;
  }>({ orgId, names: {} });
  // Client-side toggle of the server-side attention filter.
  const [attentionOnly, setAttentionOnly] = useState(false);
  // The unit / driver / attention filters belong to ONE organization: switching the
  // org clears them before the next query, so a unit of the previous org never
  // reaches the new org's report (which would answer 400). Done during render, not in
  // an effect, so no request with the stale filters is ever made.
  const [filtersOrgId, setFiltersOrgId] = useState(orgId);
  if (filtersOrgId !== orgId) {
    setFiltersOrgId(orgId);
    setUnitFilter("");
    setUserFilter("");
    setAttentionOnly(false);
    setDriverMemo({ orgId, names: {} });
  }
  // Input values vs. the range actually applied to the query (set on "Run").
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [applied, setApplied] = useState<{ start?: string; end?: string }>({});
  // False while a hand-typed date field holds text that isn't a date.
  const [datesTypable, setDatesTypable] = useState(true);
  const [hasRun, setHasRun] = useState(false);
  const queryClient = useQueryClient();

  // Don't fetch on mount — only after the user clicks "Run report". If a report for the
  // same range was already fetched earlier this session, react-query serves it from
  // cache (so it may appear immediately), but we never kick off a request just because
  // the page opened. The date range is applied on "Run report" (via `applied`); the
  // user/unit dropdowns are server-side filters that take effect immediately — changing
  // one is part of the query key, so react-query refetches once a report has been run.
  const reportQ = useQuery({
    queryKey: [
      "org", orgId, "sessions-report",
      applied.start ?? "", applied.end ?? "", userFilter, unitFilter, attentionOnly,
    ],
    queryFn: () =>
      fetchOrganizationSessionsReport(orgId, {
        ...applied,
        user_id: userFilter || undefined,
        unit_id: unitFilter || undefined,
        attention: attentionOnly || undefined,
      }),
    enabled: hasRun,
    retry: retryReport,
    // While the user explores filter combinations, re-selecting one that was already run
    // should be instant and must NOT re-hit Postgres: hold each result fresh for 60s
    // (money data moves slowly; "Run report" force-refetches when they want the latest)
    // and keep it in cache for 10min so it survives a longer back-and-forth.
    staleTime: 60_000,
    gcTime: 600_000,
  });

  // Guard shared by "Run report" and "Export CSV" — a report must never quietly fall
  // back to the default period because a typed date didn't parse.
  const rangeRejected = (range: { start?: string; end?: string }) => {
    if (!datesTypable) {
      toast.error(t("operator.orgs.report.error_date_format"));
      return true;
    }
    if (range.start && range.end && range.end < range.start) {
      toast.error(t("operator.orgs.report.error_end_before_start"));
      return true;
    }
    return false;
  };

  // Every click must produce a run. Setting `applied` to the range it already holds
  // leaves the query key untouched, so react-query serves the cached report and the
  // button looks dead — which is exactly what it looks like after re-typing the same
  // dates, or after a phrase resolves to the range that is already loaded. Same range =
  // explicit refetch. `range` is passed explicitly by the Enter key: the picker resolves
  // the keystroke ("last month" → two days) and hands the result over, because this
  // component's own start/end state hasn't re-rendered yet at that point.
  const runReport = (range?: { start?: string; end?: string }) => {
    const next = range ?? { start: start || undefined, end: end || undefined };
    if (rangeRejected(next)) return;
    // A run refreshes ONE selection; the other cached selections of this org (the
    // attention / all halves, other filters) are marked stale so the next toggle asks
    // the server again instead of showing a report older than the one just run.
    queryClient.invalidateQueries({
      queryKey: ["org", orgId, "sessions-report"],
      refetchType: "none",
    });
    const unchanged = hasRun && next.start === applied.start && next.end === applied.end;
    setApplied(next);
    setHasRun(true);
    if (unchanged) reportQ.refetch();
  };

  const report = reportQ.data;
  const rows: OrgSessionReportRow[] = report?.rows ?? [];

  const reportDrivers = report?.drivers;
  if (reportDrivers && reportDrivers !== driverMemo.from && driverMemo.orgId === orgId) {
    const names = { ...driverMemo.names };
    for (const d of reportDrivers) names[String(d.user_id)] = d.name ?? null;
    setDriverMemo({ orgId, from: reportDrivers, names });
  }
  const userOptions = useMemo(() => {
    const listed = (driverMemo.orgId === orgId ? driverMemo.from : undefined) ?? [];
    const options = [
      { value: "", label: t("operator.orgs.report.all_drivers") },
      ...listed.map((d) => ({ value: String(d.user_id), label: driverLabel(t, d.name, d.user_id) })),
    ];
    if (userFilter && !listed.some((d) => String(d.user_id) === userFilter)) {
      const name = driverLabel(t, driverMemo.names[userFilter], userFilter);
      options.push({ value: userFilter, label: t("operator.orgs.report.driver_absent", { name }) });
    }
    return options;
  }, [driverMemo, orgId, userFilter, t, language, revision]);
  // `totals` describe exactly `rows`; `period` is the whole period before the
  // attention filter (older payloads without it: the rows are the whole period).
  const totals = report?.totals;
  const period: OrgSessionReportTotals | undefined = report?.period_totals ?? totals;
  const attentionCount = period?.payment_attention_count ?? 0;
  const attentionOutstanding =
    period?.attention_known_outstanding_amount ?? period?.total_known_outstanding_amount;
  // The date fields were edited after the shown report was run.
  const datesEdited =
    hasRun && ((start || undefined) !== applied.start || (end || undefined) !== applied.end);
  const shownRange = report?.range;

  // Export = the report on screen: its rows (attention filter included), its totals,
  // the visible columns. No new request, so an unapplied date field cannot change it.
  const handleDownload = () => {
    if (!report || reportQ.isFetching) return;
    const csv = buildReportCsv(
      rows,
      totals,
      visibleReportColumns(columnVisibility, availableColumns),
      columnCtx,
    );
    // The date range the shown report actually covers. shownRange (the server's
    // resolved window) is always present once a report has loaded — including the
    // default last-31-days window — and is formatted YYYY-MM-DD, so the range always
    // appears in the name, readably (e.g. 2026-10-01_to_2026-10-22). Dashes are
    // filename-safe.
    const rs = shownRange?.start ?? applied.start;
    const re = shownRange?.end ?? applied.end;
    const span = rs || re ? `_${rs || "start"}_to_${re || "end"}` : "";
    const scope = attentionOnly ? "_attention" : "";
    // Name the download by the ORGANIZATION NAME (sanitised the same way the server's
    // full-export CSV does: keep letters/digits/-/_, other runs → _, trim _), falling
    // back to the org id only when there is no usable name.
    const orgSlug =
      (report.organization?.name ?? "")
        .replace(/[^\p{L}\p{N}\-_]+/gu, "_")
        .replace(/^_+|_+$/g, "") || `org-${orgId}`;
    const filename = `${orgSlug}_sessions${span}${scope}.csv`;
    // Prepend a BOM so Excel reads UTF-8 names (e.g. accented drivers) correctly.
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(t("operator.orgs.report.downloaded"));
  };

  return (
    <TooltipProvider delayDuration={100}>
      <Card>
        <CardHeader>
          <CardTitle>{t("operator.orgs.report.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Controls */}
          <div className="flex flex-wrap items-center gap-2">
            <DateRangePicker
              start={start || undefined}
              end={end || undefined}
              onChange={(s, e) => {
                setStart(s ?? "");
                setEnd(e ?? "");
              }}
              onValidityChange={setDatesTypable}
              onSubmit={(s, e) => runReport({ start: s, end: e })}
            />
            <Button size="sm" onClick={() => runReport()} disabled={reportQ.isFetching}>
              {reportQ.isFetching ? t("operator.orgs.report.loading") : t("operator.orgs.report.run")}
            </Button>
            {/* Server-side filters. Default "All" keeps the report org-wide. */}
            <SearchableSelect
              options={userOptions}
              value={userFilter}
              onValueChange={setUserFilter}
              placeholder={t("operator.orgs.report.all_drivers")}
              searchPlaceholder={t("operator.orgs.report.filter_driver")}
              emptyText={t("operator.orgs.report.drivers_empty", { state: report ? "loaded" : "not_run" })}
            />
            <SearchableSelect
              options={unitOptions}
              value={unitFilter}
              onValueChange={setUnitFilter}
              placeholder={t("operator.orgs.report.all_units")}
              searchPlaceholder={t("operator.orgs.report.filter_unit")}
              emptyText={t("operator.orgs.report.no_units")}
            />
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownload}
              disabled={!report || reportQ.isFetching || reportQ.isError}
              title={t("operator.orgs.report.export_title")}
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              {t("operator.orgs.report.export_csv")}
            </Button>
            {/* Choose which columns the table (and the CSV export) show. */}
            <ColumnChooser
              columns={chooserColumns}
              visibility={columnVisibility}
              onToggle={handleColumnToggle}
              onReset={resetToDefaults}
            />
          </div>
          <div className="space-y-0.5 text-xs opacity-70">
            <div>{t("operator.orgs.report.selection_help")}</div>
            {report && shownRange && (
              <div data-testid="shown-range">
                {t("operator.orgs.report.shown_range", {
                  start: shownRange.start,
                  end: shownRange.end,
                  defaulted: shownRange.defaulted ? "yes" : "no",
                  attention: attentionOnly ? "yes" : "no",
                })}
              </div>
            )}
            {datesEdited && report && (
              <div className="text-amber-700 dark:text-amber-400" data-testid="dates-edited">
                {t("operator.orgs.report.dates_edited")}
              </div>
            )}
            <div>{t("operator.orgs.report.drivers_help")}</div>
            <div>{t("operator.orgs.report.csv_help")}</div>
          </div>

          {/* Payment-attention banner, from the PERIOD totals so it reads the same with
              the filter on or off. The count and the dollar figure are the same rows:
              CONFIRMED outstanding (finalized amount due minus captured) on the
              sessions that need attention; those whose final charge is unknown are a
              COUNT — never dollarised, so an authorization hold can never masquerade
              as money owed. All known outstanding of the period (terminal rows such as
              migrated uncollected included) is a separate line. While the filter is
              on, the banner stays whatever the result (no attention rows left, a range
              or unit without any, an error), so the filter can always be turned off. */}
          {((report && attentionCount > 0) || attentionOnly) && (
            <div
              className="flex flex-wrap items-center gap-2 rounded-md border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400"
              data-testid="attention-banner"
            >
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {!(report && attentionCount > 0) ? (
                <span>{t("operator.orgs.report.attention_filter_note", { state: report ? "empty" : "pending" })}</span>
              ) : (
                <span>
                  {/* One sentence per catalog message (no fragments); the confirmed amount
                      stays a dollar figure and the unknown ones stay a count. */}
                  {t("operator.orgs.report.attention_period", {
                    count: attentionCount,
                    amount: money(attentionOutstanding),
                    unknown: period?.unknown_outstanding_session_count ?? 0,
                  })}
                  {period && (
                    <span className="opacity-80">
                      {" "}
                      {t("operator.orgs.report.attention_breakdown", {
                        action: String(period.payment_action_required_count),
                        engineering: String(period.engineering_investigation_count),
                        review: String(period.manual_review_required_count),
                      })}
                    </span>
                  )}
                  {period && period.total_known_outstanding_amount !== attentionOutstanding && (
                    <span className="block opacity-80" data-testid="period-outstanding">
                      {t("operator.orgs.report.period_outstanding", {
                        amount: money(period.total_known_outstanding_amount),
                        count: period.known_outstanding_session_count,
                      })}
                    </span>
                  )}
                </span>
              )}
              <Button
                size="xs"
                variant={attentionOnly ? "default" : "outline"}
                className="ml-auto"
                onClick={() => setAttentionOnly((v) => !v)}
              >
                {t(attentionOnly ? "operator.orgs.report.show_all" : "operator.orgs.report.show_attention")}
              </Button>
            </div>
          )}

          {/* Table */}
          {reportQ.isError ? (
            <div className="text-sm text-error" role="alert">
              {errorMessage(t, reportQ.error, { fallback: "operator.orgs.report.error_load_generic" })}{" "}
              {t("operator.orgs.report.load_error_hint")}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    {visibleCols.map((c) => (
                      <th
                        key={c.id}
                        className={`px-3 py-2 font-medium ${c.align === "right" ? "text-right" : ""} ${c.headerTitleKey ? "cursor-help" : ""}`}
                        title={c.headerTitleKey ? t(c.headerTitleKey) : undefined}
                      >
                        {t(c.labelKey)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reportQ.isFetching ? (
                    <tr>
                      <td colSpan={colCount} className="px-3 py-6 text-center opacity-60">{t("operator.orgs.report.loading")}</td>
                    </tr>
                  ) : !report ? (
                    <tr>
                      <td colSpan={colCount} className="px-3 py-6 text-center opacity-60">
                        {t("operator.orgs.report.prompt")}
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={colCount} className="px-3 py-6 text-center opacity-60">
                        {t(attentionOnly ? "operator.orgs.report.empty_attention" : "operator.orgs.report.empty")}
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, i) => (
                      <tr key={i} className="border-t border-border hover:bg-muted/30">
                        {visibleCols.map((c) => (
                          <td
                            key={c.id}
                            className={`px-3 py-1.5 ${c.align === "right" ? "text-right" : ""} ${c.mono ? "font-mono whitespace-nowrap" : ""}`}
                            title={c.id === "unit" ? r.unit_id : undefined}
                          >
                            {c.cell(r, cellCtx)}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
                {totals && rows.length > 0 && (
                  <tfoot className="border-t-2 border-border bg-muted/40 font-semibold">
                    <TotalsRow
                      cols={visibleCols}
                      totals={totals}
                      label={attentionOnly
                        ? t("operator.orgs.report.totals_scope", { scope: "shown" })
                        : t("operator.orgs.report.totals")}
                      t={t}
                    />
                    {attentionOnly && period && period !== totals && (
                      <TotalsRow
                        cols={visibleCols}
                        totals={period}
                        label={t("operator.orgs.report.totals_scope", { scope: "period" })}
                        t={t}
                        muted
                      />
                    )}
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}

/** A driver option's label: the name the rows show, or the user id when the driver
 *  has no name at all. */
function driverLabel(t: Translate, name: string | null | undefined, userId: string | number): string {
  return name ?? t("operator.orgs.report.driver_fallback", { id: String(userId) });
}

/** One totals line. `totals` must be the totals of the selection the line names. The
 *  label never takes a total's place: it fills the first cell when that column has no
 *  total, otherwise it is a caption drawn above the first cell's total (CSS-generated,
 *  so the cell's text stays the bare total). */
function TotalsRow({
  cols,
  totals,
  label,
  t,
  muted = false,
}: {
  cols: ReturnType<typeof visibleReportColumns>;
  totals: OrgSessionReportTotals;
  label: string;
  t: Translate;
  muted?: boolean;
}) {
  const captioned = !!cols[0]?.total;
  return (
    <tr
      className={muted ? "font-normal opacity-70" : undefined}
      data-testid={muted ? "period-totals" : "totals"}
      aria-label={label}
    >
      {cols.map((c, i) => (
        <td
          key={c.id}
          className={`px-3 py-2 ${c.align === "right" ? "text-right" : ""} ${
            i === 0 && captioned
              ? "before:block before:text-[10px] before:font-normal before:uppercase before:opacity-60 before:content-[attr(data-label)]"
              : ""
          }`}
          data-label={i === 0 && captioned ? label : undefined}
        >
          {c.total ? c.total(totals, t) : i === 0 ? label : ""}
        </td>
      ))}
    </tr>
  );
}
